import React, { useState } from "react";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggle = (i) => setActiveSection(activeSection === i ? null : i);

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoBox}>
            <span style={styles.logoText}>T</span>
          </div>
          <h1 style={styles.title}>Tracklio DPC</h1>
          <p style={styles.subtitle}>Privacy Policy</p>
          <p style={styles.date}>Last updated: June 25, 2026</p>
        </div>

        {/* Intro Banner */}
        <div style={styles.introBanner}>
          <span style={styles.bannerIcon}>🏢</span>
          <p style={styles.bannerText}>
            This is an <strong>Enterprise Device Management</strong> application intended for use only on
            company-owned Android devices. It is deployed by organizations through Android Enterprise
            Device Owner (DPC) provisioning.
          </p>
        </div>

        <div style={styles.content}>

          {/* 1. Overview */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Overview</h2>
            <p style={styles.text}>
              Tracklio DPC ("the App") is an Enterprise Mobile Device Management (MDM) application
              built for corporate device management. When installed on a company-owned Android device
              as a Device Policy Controller (DPC), it enforces organizational security policies,
              enables remote management, and monitors device compliance. This Privacy Policy describes
              what data the App accesses, how it is used, and how it is protected.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
            <p style={styles.text}>
              Tracklio DPC collects the following categories of device information:
            </p>

            <div style={styles.permBlock}>
              <div style={styles.permHeader}>📱 Device Identity &amp; Hardware</div>
              <ul style={styles.list}>
                <li style={styles.listItem}>Device model, manufacturer, brand</li>
                <li style={styles.listItem}>Android OS version and SDK level</li>
                <li style={styles.listItem}>Device serial number</li>
                <li style={styles.listItem}>Android ID (unique device identifier)</li>
                <li style={styles.listItem}>
                  IMEI / MEID number — collected via <code style={styles.code}>READ_PHONE_STATE</code> permission,
                  used solely for device registration and identification in the MDM system
                </li>
                <li style={styles.listItem}>Wi-Fi network information (SSID, connection state) — for network diagnostics</li>
                <li style={styles.listItem}>IP address</li>
              </ul>
            </div>

            <div style={styles.permBlock}>
              <div style={styles.permHeader}>📍 Location Data</div>
              <p style={styles.text}>
                The App requests the following location permissions:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <strong>Precise Location</strong> (<code style={styles.code}>ACCESS_FINE_LOCATION</code>) — GPS-based location for
                  asset tracking of company-owned devices
                </li>
                <li style={styles.listItem}>
                  <strong>Approximate Location</strong> (<code style={styles.code}>ACCESS_COARSE_LOCATION</code>) — Network/cell-based
                  location as a fallback
                </li>
                <li style={styles.listItem}>
                  <strong>Background Location</strong> (<code style={styles.code}>ACCESS_BACKGROUND_LOCATION</code>) — Required to
                  report device location to IT administrators even when the app is not in the foreground.
                  This is used exclusively for device asset tracking on company-owned hardware and is
                  only active when enabled by the organization's IT policy.
                </li>
              </ul>
              <div style={styles.noteBox}>
                ⚠️ Location data is transmitted only to your organization's authorized IT administrators
                via the Tracklio Hub MDM platform. It is never shared with third parties or used for advertising.
              </div>
            </div>

            <div style={styles.permBlock}>
              <div style={styles.permHeader}>🔔 Notifications &amp; Foreground Service</div>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <strong>POST_NOTIFICATIONS</strong> — To display MDM status notifications to the device user,
                  such as policy updates, lock events, or admin messages
                </li>
                <li style={styles.listItem}>
                  <strong>FOREGROUND_SERVICE</strong> — The App runs a persistent foreground service
                  (<code style={styles.code}>KeepAliveService</code>) to ensure continuous device policy enforcement
                  and communication with the MDM server. This service displays a visible notification
                  as required by Android.
                </li>
              </ul>
            </div>

            <div style={styles.permBlock}>
              <div style={styles.permHeader}>⚡ System &amp; Device Control</div>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <strong>RECEIVE_BOOT_COMPLETED</strong> — To automatically restart device management
                  services after device reboot, ensuring continuous policy enforcement
                </li>
                <li style={styles.listItem}>
                  <strong>WAKE_LOCK</strong> — To keep the device processor active during critical
                  MDM operations such as policy sync and remote commands
                </li>
                <li style={styles.listItem}>
                  <strong>REQUEST_IGNORE_BATTERY_OPTIMIZATIONS</strong> — To prevent Android from
                  killing the MDM service in the background, ensuring uninterrupted device management
                </li>
                <li style={styles.listItem}>
                  <strong>SYSTEM_ALERT_WINDOW</strong> — To display administrative overlays (such as
                  device lock screens or compliance notices) on top of other apps, as required for DPC functionality
                </li>
                <li style={styles.listItem}>
                  <strong>BIND_DEVICE_ADMIN</strong> — Core Device Owner permission required for all
                  MDM policy enforcement capabilities
                </li>
              </ul>
            </div>

            <div style={styles.permBlock}>
              <div style={styles.permHeader}>🌐 Network</div>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  <strong>INTERNET</strong> — To communicate with the Tracklio Hub MDM server for
                  policy sync, remote commands, and device status reporting
                </li>
                <li style={styles.listItem}>
                  <strong>ACCESS_NETWORK_STATE / ACCESS_WIFI_STATE</strong> — To check connectivity
                  before attempting server communication and for network diagnostics
                </li>
              </ul>
            </div>
          </section>

          {/* 3. How We Use This Information */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>3. How We Use This Information</h2>
            <p style={styles.text}>All collected information is used exclusively for the following purposes:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Device enrollment and registration in your organization's MDM system</li>
              <li style={styles.listItem}>Unique device identification for management purposes</li>
              <li style={styles.listItem}>Applying and enforcing corporate security policies</li>
              <li style={styles.listItem}>Remote device management by authorized IT administrators</li>
              <li style={styles.listItem}>Asset tracking and location reporting for company-owned devices</li>
              <li style={styles.listItem}>Compliance monitoring and policy enforcement</li>
              <li style={styles.listItem}>Delivering administrative notifications and alerts</li>
              <li style={styles.listItem}>Maintaining persistent management service after device restarts</li>
            </ul>
            <div style={styles.noteBox}>
              ℹ️ This data is <strong>never</strong> used for advertising, profiling, analytics sold to
              third parties, or any purpose beyond corporate device management.
            </div>
          </section>

          {/* 4. Device Management Capabilities */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>4. Device Management Capabilities</h2>
            <p style={styles.text}>
              As a Device Owner (DPC) application, Tracklio DPC may perform the following actions
              as directed by your organization's IT administrators:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Lock the device screen remotely</li>
              <li style={styles.listItem}>Display policy compliance overlays using system alert window</li>
              <li style={styles.listItem}>Enforce password, encryption, and screen timeout policies</li>
              <li style={styles.listItem}>Restrict access to specific device settings or applications</li>
              <li style={styles.listItem}>Track device location when enabled by the organization</li>
              <li style={styles.listItem}>Wipe device data upon unenrollment (factory reset)</li>
              <li style={styles.listItem}>Manage device settings as configured by IT administrators</li>
            </ul>
            <div style={styles.noteBox}>
              ⚠️ All device management actions are initiated exclusively by your organization's
              authorized IT administrators. Tracklio Hub does not take any autonomous action on
              devices without administrator instruction.
            </div>
          </section>

          {/* 5. Background Location - Special Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Background Location — Special Disclosure</h2>
            <p style={styles.text}>
              In compliance with Google Play policy, we are providing a prominent disclosure about
              background location access:
            </p>
            <div style={styles.highlightBox}>
              <p style={styles.highlightText}>
                Tracklio DPC collects location data in the background to enable <strong>device asset
                tracking</strong> for company-owned Android devices managed by enterprise IT administrators.
              </p>
              <p style={styles.highlightText}>
                Background location is collected <strong>only when</strong>:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>The device is enrolled in the organization's MDM system</li>
                <li style={styles.listItem}>The organization's IT policy has enabled location tracking</li>
                <li style={styles.listItem}>The device is a company-owned asset (not personal use)</li>
              </ul>
              <p style={styles.highlightText}>
                Location data is transmitted securely to <strong>Tracklio Hub servers</strong> and is
                accessible only to your organization's authorized IT administrators. It is never used
                for advertising or shared with any external parties.
              </p>
            </div>
          </section>

          {/* 6. Data Storage and Security */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Data Storage and Security</h2>
            <p style={styles.text}>All data collected by Tracklio DPC is:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Transmitted securely using HTTPS/TLS encryption</li>
              <li style={styles.listItem}>Stored on secure servers located in India</li>
              <li style={styles.listItem}>Accessible only to authorized IT administrators of your organization</li>
              <li style={styles.listItem}>Never sold, rented, or shared with third parties for any purpose</li>
              <li style={styles.listItem}>Retained only for the duration that the device remains enrolled in the MDM system</li>
              <li style={styles.listItem}>Protected by access controls and authentication on the Tracklio Hub platform</li>
            </ul>
          </section>

          {/* 7. Data Sharing */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Data Sharing</h2>
            <p style={styles.text}>
              Tracklio DPC does <strong>not</strong> share any collected data with third parties.
              Data is only accessible to:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Your organization's authorized IT administrators</li>
              <li style={styles.listItem}>The Tracklio Hub MDM platform operated by your organization</li>
            </ul>
            <p style={styles.text}>
              We do not integrate with advertising networks, analytics platforms, or any external
              data brokers.
            </p>
          </section>

          {/* 8. Enterprise Deployment */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Enterprise Deployment</h2>
            <p style={styles.text}>
              Tracklio DPC is intended exclusively for deployment on <strong>company-owned devices</strong>
              through Android Enterprise Device Owner (DO) provisioning via QR code or NFC. By using
              this application, the device user acknowledges that:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>This device is company property</li>
              <li style={styles.listItem}>The organization's IT team manages this device</li>
              <li style={styles.listItem}>Corporate policies, including location tracking, apply to this device</li>
              <li style={styles.listItem}>This app is not intended for personal or consumer use</li>
            </ul>
          </section>

          {/* 9. Permissions Summary */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Permissions Summary</h2>
            <p style={styles.text}>Complete list of Android permissions used by Tracklio DPC:</p>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Permission</th>
                  <th style={styles.th}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["INTERNET", "Communicate with Tracklio Hub MDM server"],
                  ["ACCESS_NETWORK_STATE", "Check network connectivity before sync"],
                  ["ACCESS_WIFI_STATE", "Wi-Fi diagnostics and network info"],
                  ["READ_PHONE_STATE", "Read IMEI for device registration"],
                  ["RECEIVE_BOOT_COMPLETED", "Restart MDM service after device reboot"],
                  ["ACCESS_FINE_LOCATION", "Precise GPS location for asset tracking"],
                  ["ACCESS_COARSE_LOCATION", "Approximate location as fallback"],
                  ["ACCESS_BACKGROUND_LOCATION", "Background location for continuous asset tracking"],
                  ["POST_NOTIFICATIONS", "Show MDM status and admin notifications"],
                  ["FOREGROUND_SERVICE", "Run persistent device management service"],
                  ["WAKE_LOCK", "Keep processor active during MDM operations"],
                  ["REQUEST_IGNORE_BATTERY_OPTIMIZATIONS", "Prevent system from killing MDM service"],
                  ["SYSTEM_ALERT_WINDOW", "Display admin overlays and lock screens"],
                  ["BIND_DEVICE_ADMIN", "Core Device Owner / DPC functionality"],
                ].map(([perm, purpose], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#f8f9fa" : "#ffffff" }}>
                    <td style={styles.tdCode}><code style={styles.code}>{perm}</code></td>
                    <td style={styles.td}>{purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* 10. Data Deletion */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Data Deletion</h2>
            <p style={styles.text}>When a device is unenrolled from the MDM system:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>All device management policies are immediately removed</li>
              <li style={styles.listItem}>Device data is deleted from Tracklio Hub servers within 30 days</li>
              <li style={styles.listItem}>Location history is permanently purged</li>
              <li style={styles.listItem}>The app can be uninstalled by the device user post-unenrollment</li>
            </ul>
            <p style={styles.text}>
              Organizations may also request immediate data deletion by contacting us at the address below.
            </p>
          </section>

          {/* 11. Children's Privacy */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>11. Children's Privacy</h2>
            <p style={styles.text}>
              Tracklio DPC is an enterprise application intended exclusively for use on corporate devices
              by working professionals. It is not directed at children under the age of 13, and we do not
              knowingly collect any data from children.
            </p>
          </section>

          {/* 12. Contact */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>12. Contact Us</h2>
            <p style={styles.text}>
              For questions, data deletion requests, or concerns about this Privacy Policy:
            </p>
            <div style={styles.contactBox}>
              <p style={styles.contactItem}>🌐 Website: <a href="https://trackliohub.com" style={styles.link}>trackliohub.com</a></p>
              <p style={styles.contactItem}>📧 Email: <a href="mailto:privacy@trackliohub.com" style={styles.link}>privacy@trackliohub.com</a></p>
              <p style={styles.contactItem}>📍 Address: Delhi, India</p>
            </div>
          </section>

          {/* 13. Changes */}
          <section style={{ ...styles.section, borderBottom: "none" }}>
            <h2 style={styles.sectionTitle}>13. Changes to This Policy</h2>
            <p style={styles.text}>
              We may update this Privacy Policy periodically. Material changes will be communicated
              through the Tracklio Hub admin console and posted on this page with an updated revision
              date. Continued use of the application after changes constitutes acceptance of the
              updated policy.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>© 2026 Tracklio Hub. All rights reserved.</p>
          <p style={styles.footerText}>Applies to: Tracklio DPC Android Application · Package: com.trackliohub.dpc</p>
        </div>

      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#eef2f7",
    padding: "24px 16px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  card: {
    maxWidth: "860px",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
    padding: "44px 32px",
    textAlign: "center",
    color: "#ffffff",
  },
  logoBox: {
    width: "68px",
    height: "68px",
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  logoText: {
    fontSize: "34px",
    fontWeight: "700",
    color: "#ffffff",
    display: "block",
    lineHeight: "68px",
    textAlign: "center",
  },
  title: { fontSize: "28px", fontWeight: "700", margin: "0 0 6px", color: "#ffffff" },
  subtitle: { fontSize: "18px", margin: "0 0 8px", color: "rgba(255,255,255,0.85)", fontWeight: "500" },
  date: { fontSize: "13px", color: "rgba(255,255,255,0.6)", margin: "0" },

  introBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#e8f0fe",
    borderLeft: "4px solid #1a73e8",
    padding: "16px 24px",
    margin: "0",
  },
  bannerIcon: { fontSize: "20px", flexShrink: 0, marginTop: "2px" },
  bannerText: { fontSize: "14px", color: "#1a3a6b", lineHeight: "1.6", margin: 0 },

  content: { padding: "32px" },

  section: {
    marginBottom: "32px",
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: "28px",
  },
  sectionTitle: {
    fontSize: "17px",
    fontWeight: "600",
    color: "#1a73e8",
    marginBottom: "14px",
    marginTop: "0",
  },
  text: { fontSize: "15px", color: "#444", lineHeight: "1.75", margin: "0 0 12px" },

  permBlock: {
    backgroundColor: "#f8faff",
    border: "1px solid #dce8ff",
    borderRadius: "8px",
    padding: "14px 18px",
    marginBottom: "14px",
  },
  permHeader: {
    fontWeight: "600",
    fontSize: "14px",
    color: "#1a4fa0",
    marginBottom: "10px",
  },

  noteBox: {
    fontSize: "13px",
    color: "#5a5a00",
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe082",
    borderRadius: "6px",
    padding: "12px 14px",
    marginTop: "10px",
    lineHeight: "1.6",
  },
  highlightBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: "8px",
    padding: "18px 20px",
  },
  highlightText: {
    fontSize: "14px",
    color: "#4a2700",
    lineHeight: "1.7",
    margin: "0 0 10px",
  },

  list: { margin: "8px 0 0 0", paddingLeft: "22px" },
  listItem: { fontSize: "15px", color: "#444", lineHeight: "1.75", marginBottom: "5px" },

  code: {
    fontFamily: "monospace",
    fontSize: "12px",
    backgroundColor: "#eef2ff",
    color: "#1a3a9a",
    padding: "1px 5px",
    borderRadius: "4px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
    marginTop: "10px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    overflow: "hidden",
  },
  th: {
    backgroundColor: "#1a73e8",
    color: "#fff",
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "13px",
  },
  td: { padding: "9px 14px", color: "#444", verticalAlign: "top", borderTop: "1px solid #e8e8e8" },
  tdCode: { padding: "9px 14px", verticalAlign: "top", borderTop: "1px solid #e8e8e8", whiteSpace: "nowrap" },

  contactBox: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    padding: "16px 20px",
    marginTop: "12px",
  },
  contactItem: { fontSize: "15px", color: "#444", margin: "6px 0" },
  link: { color: "#1a73e8", textDecoration: "none" },

  footer: {
    backgroundColor: "#f8f9fa",
    padding: "20px 32px",
    textAlign: "center",
    borderTop: "1px solid #e0e0e0",
  },
  footerText: { fontSize: "13px", color: "#888", margin: "4px 0" },
};

export default PrivacyPolicy;